import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, ArrowRight, Check, Microscope, Clock, FileQuestion,
  ChevronRight, Brain, Moon, Activity, Heart, BarChart3, TrendingDown, TrendingUp, Minus
} from "lucide-react";
import { toast } from "sonner";
import { PremiumPageGuard } from "../components/PremiumPageGuard";

const CATEGORY_CONFIG = {
  mental_health: { icon: Brain, color: "text-indigo-500", bg: "bg-indigo-50", border: "border-indigo-200" },
  stress: { icon: Activity, color: "text-amber-500", bg: "bg-amber-50", border: "border-amber-200" },
  wellbeing: { icon: Heart, color: "text-rose-500", bg: "bg-rose-50", border: "border-rose-200" },
  sleep: { icon: Moon, color: "text-sky-500", bg: "bg-sky-50", border: "border-sky-200" },
};

const SEVERITY_COLORS = {
  minimal: "text-emerald-600 bg-emerald-50",
  mild: "text-lime-600 bg-lime-50",
  low: "text-emerald-600 bg-emerald-50",
  moderate: "text-amber-600 bg-amber-50",
  moderate_low: "text-amber-600 bg-amber-50",
  moderately_severe: "text-orange-600 bg-orange-50",
  high: "text-emerald-600 bg-emerald-50",
  good: "text-emerald-600 bg-emerald-50",
  poor: "text-orange-600 bg-orange-50",
  severe: "text-red-600 bg-red-50",
  very_poor: "text-red-600 bg-red-50",
};

// ========== QUESTIONNAIRE LIST VIEW ==========
const QuestionnaireList = ({ questionnaires, latestResults, onSelect }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold uppercase tracking-wider mb-4">
          <Microscope className="w-4 h-4" strokeWidth={1.5} />
          {t("deepDive.subtitle")}
        </div>
        <h1 className="text-3xl sm:text-4xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
          {t("deepDive.title")}
        </h1>
        <p className="text-slate-500 max-w-lg mx-auto text-sm leading-relaxed">
          {t("deepDive.description")}
        </p>
      </div>

      <div className="grid gap-4">
        {questionnaires.map((q) => {
          const config = CATEGORY_CONFIG[q.category] || CATEGORY_CONFIG.mental_health;
          const Icon = config.icon;
          const latest = latestResults[q.id];

          return (
            <button
              key={q.id}
              onClick={() => onSelect(q.id)}
              className={`w-full text-left glass-card glass-card-hover p-5 sm:p-6 border ${config.border} transition-all duration-300`}
              data-testid={`deepdive-select-${q.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${config.bg} shrink-0`}>
                  <Icon className={`w-6 h-6 ${config.color}`} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-medium text-sky-900" style={{ fontFamily: "Outfit" }}>
                      {q.name}
                    </h3>
                    <span className="text-xs text-slate-400">{q.full_name}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">{q.description}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <FileQuestion className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {q.question_count} {t("deepDive.questions")}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                      ~{q.estimated_minutes} {t("common.minutes")}
                    </span>
                  </div>
                  {latest && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                      <span className="text-xs text-slate-400">{t("deepDive.lastResult")}:</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${SEVERITY_COLORS[latest.severity_level] || "text-slate-600 bg-slate-50"}`}>
                        {latest.display_score}/{latest.max_score} — {latest.severity_label}
                      </span>
                      <span className="text-xs text-slate-300 ml-auto">{latest.date}</span>
                    </div>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-1" strokeWidth={1.5} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ========== ACTIVE QUESTIONNAIRE VIEW ==========
const ActiveQuestionnaire = ({ questionnaireType, onComplete, onBack }) => {
  const { t } = useTranslation();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/deepdive/questionnaire/${questionnaireType}`);
        setQuestionnaire(res.data);
        const init = {};
        res.data.questions.forEach((q) => { init[q.id] = null; });
        setAnswers(init);
      } catch {
        toast.error("Failed to load questionnaire");
        onBack();
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [questionnaireType, onBack]);

  const handleSelect = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/deepdive/submit`, {
        questionnaire_type: questionnaireType,
        responses: answers,
      });
      toast.success(t("deepDive.submitted"));
      onComplete(res.data);
    } catch (err) {
      toast.error("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !questionnaire) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-14 h-14 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  const q = questionnaire.questions[currentStep];
  const progress = ((currentStep + 1) / questionnaire.questions.length) * 100;
  const canProceed = answers[q.id] !== null && answers[q.id] !== undefined;

  return (
    <div data-testid="deepdive-active-questionnaire">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <button onClick={onBack} className="text-sky-600 hover:text-sky-800 text-sm flex items-center gap-1" data-testid="deepdive-back-to-list">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("common.back")}
          </button>
          <span className="text-xs text-sky-500">
            {questionnaire.name} — {currentStep + 1}/{questionnaire.questions.length}
          </span>
        </div>
        <div className="h-1.5 bg-sky-100 rounded-full overflow-hidden">
          <div className="h-full bg-sky-400 transition-all duration-500 ease-out rounded-full" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Preamble note */}
      {currentStep === 0 && (
        <div className="mb-6 p-4 rounded-xl bg-sky-50/60 border border-sky-100 text-sm text-sky-700 leading-relaxed">
          {t("deepDive.preamble")}
        </div>
      )}

      {/* Question Card */}
      <div className="glass-card p-8 sm:p-10 animate-fade-in" key={q.id} data-testid={`deepdive-question-${q.id}`}>
        <p className="text-xl sm:text-2xl font-light text-sky-900 mb-8 leading-relaxed" style={{ fontFamily: "Outfit" }}>
          {q.text}
        </p>

        <div className="space-y-3">
          {q.options.map((opt) => {
            const selected = answers[q.id] === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => handleSelect(q.id, opt.value)}
                className={`w-full text-left px-5 py-3.5 rounded-xl border transition-all duration-200 ${
                  selected
                    ? "border-sky-400 bg-sky-50 text-sky-800 shadow-sm"
                    : "border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/30 text-slate-600"
                }`}
                data-testid={`deepdive-option-${q.id}-${opt.value}`}
              >
                <span className="text-base">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button
          variant="ghost"
          onClick={() => setCurrentStep((p) => Math.max(0, p - 1))}
          disabled={currentStep === 0}
          className="text-sky-600 hover:text-sky-800"
          data-testid="deepdive-prev-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          {t("questionnaire.previous")}
        </Button>

        {currentStep === questionnaire.questions.length - 1 ? (
          <Button
            onClick={handleSubmit}
            disabled={!canProceed || submitting}
            className="glow-button"
            data-testid="deepdive-submit-btn"
          >
            {submitting ? (
              <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" strokeWidth={1.5} />
                {t("deepDive.getResults")}
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={() => setCurrentStep((p) => Math.min(questionnaire.questions.length - 1, p + 1))}
            disabled={!canProceed}
            className="glow-button"
            data-testid="deepdive-next-btn"
          >
            {t("common.next")}
            <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
          </Button>
        )}
      </div>
    </div>
  );
};

// ========== RESULTS VIEW ==========
const ResultView = ({ result, onBack, onRetake }) => {
  const { t } = useTranslation();
  const scorePercent = Math.round((result.display_score / result.max_score) * 100);

  const getTrendIcon = () => {
    if (scorePercent > 60) return <TrendingUp className="w-5 h-5 text-emerald-500" />;
    if (scorePercent < 40) return <TrendingDown className="w-5 h-5 text-orange-500" />;
    return <Minus className="w-5 h-5 text-amber-500" />;
  };

  return (
    <div data-testid="deepdive-result-view" className="animate-fade-in">
      <button onClick={onBack} className="text-sky-600 hover:text-sky-800 text-sm flex items-center gap-1 mb-6" data-testid="deepdive-result-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("deepDive.backToList")}
      </button>

      {/* Score Card */}
      <div className="glass-card p-8 sm:p-10 text-center mb-6">
        <h2 className="text-xl font-light text-sky-800 mb-2" style={{ fontFamily: "Outfit" }}>
          {result.questionnaire_name}
        </h2>
        <p className="text-xs text-slate-400 mb-6">{result.date}</p>

        {/* Score Ring */}
        <div className="relative w-36 h-36 mx-auto mb-6">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" stroke="#e2e8f0" strokeWidth="8" fill="none" />
            <circle
              cx="60" cy="60" r="52"
              stroke="url(#scoreGrad)"
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${(scorePercent / 100) * 327} 327`}
              className="transition-all duration-1000 ease-out"
            />
            <defs>
              <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#818cf8" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
              {result.display_score}
            </span>
            <span className="text-xs text-slate-400">/ {result.max_score}</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-4">
          {getTrendIcon()}
          <span className={`text-sm font-medium px-3 py-1 rounded-full ${SEVERITY_COLORS[result.severity_level] || "text-slate-600 bg-slate-50"}`}>
            {result.severity_label}
          </span>
        </div>
      </div>

      {/* AI Interpretation */}
      {result.ai_interpretation && (
        <div className="glass-card p-6 sm:p-8 mb-6">
          <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Brain className="w-5 h-5 text-indigo-400" strokeWidth={1.5} />
            {t("deepDive.aiInsight")}
          </h3>
          <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
            {result.ai_interpretation}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-100 text-xs text-amber-700 leading-relaxed mb-6">
        {t("deepDive.disclaimer")}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1" data-testid="deepdive-result-done">
          {t("deepDive.backToList")}
        </Button>
        <Button onClick={() => onRetake(result.questionnaire_type)} className="flex-1 glow-button" data-testid="deepdive-retake-btn">
          {t("deepDive.retake")}
        </Button>
      </div>
    </div>
  );
};

// ========== HISTORY VIEW ==========
const HistoryView = ({ onBack }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchHistory = useCallback(async () => {
    try {
      const url = filter ? `${API}/deepdive/history?questionnaire_type=${filter}` : `${API}/deepdive/history`;
      const res = await axios.get(url);
      setHistory(res.data.results);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <div data-testid="deepdive-history">
      <button onClick={onBack} className="text-sky-600 hover:text-sky-800 text-sm flex items-center gap-1 mb-6" data-testid="deepdive-history-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("common.back")}
      </button>
      <h2 className="text-2xl font-light text-sky-900 mb-4" style={{ fontFamily: "Outfit" }}>
        {t("deepDive.history")}
      </h2>

      {/* Filter buttons */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button
          onClick={() => setFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!filter ? "bg-sky-400 text-white" : "bg-sky-50 text-sky-600 hover:bg-sky-100"}`}
          data-testid="deepdive-filter-all"
        >
          {t("deepDive.all")}
        </button>
        {["phq9", "gad7", "pss10", "who5", "psqi"].map((qtype) => (
          <button
            key={qtype}
            onClick={() => setFilter(qtype)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === qtype ? "bg-sky-400 text-white" : "bg-sky-50 text-sky-600 hover:bg-sky-100"}`}
            data-testid={`deepdive-filter-${qtype}`}
          >
            {qtype.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 rounded-full bg-sky-400/30 glow-pulse" /></div>
      ) : history.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">{t("deepDive.noHistory")}</div>
      ) : (
        <div className="space-y-3">
          {history.map((r) => (
            <div key={r.id} className="glass-card p-4 flex items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-sky-900">{r.questionnaire_name}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${SEVERITY_COLORS[r.severity_level] || "text-slate-500 bg-slate-50"}`}>
                    {r.display_score}/{r.max_score}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{r.severity_label} — {r.date}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== MAIN PAGE ==========
const DeepDive = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState("list"); // list | active | result | history
  const [questionnaires, setQuestionnaires] = useState([]);
  const [latestResults, setLatestResults] = useState({});
  const [activeType, setActiveType] = useState(null);
  const [currentResult, setCurrentResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [listRes, latestRes] = await Promise.all([
        axios.get(`${API}/deepdive/questionnaires`),
        axios.get(`${API}/deepdive/latest`),
      ]);
      setQuestionnaires(listRes.data.questionnaires);
      setLatestResults(latestRes.data.latest);
    } catch {
      toast.error("Failed to load questionnaires");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelect = (qtype) => {
    setActiveType(qtype);
    setView("active");
  };

  const handleComplete = (result) => {
    setCurrentResult(result);
    setView("result");
    // Refresh latest results
    fetchData();
  };

  const handleRetake = (qtype) => {
    setActiveType(qtype);
    setView("active");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="deepdive-page">
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-72 h-72 bg-sky-300/10 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              if (view === "list") navigate("/dashboard");
              else setView("list");
            }}
            className="text-sky-600 hover:text-sky-800"
            data-testid="deepdive-header-back"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {view === "list" ? t("common.dashboard") : t("deepDive.title")}
          </Button>
          {view === "list" && (
            <Button variant="ghost" onClick={() => setView("history")} className="text-sky-500 text-sm" data-testid="deepdive-view-history">
              <BarChart3 className="w-4 h-4 mr-1" strokeWidth={1.5} />
              {t("deepDive.history")}
            </Button>
          )}
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          {view === "list" && (
            <QuestionnaireList
              questionnaires={questionnaires}
              latestResults={latestResults}
              onSelect={handleSelect}
            />
          )}
          {view === "active" && (
            <ActiveQuestionnaire
              questionnaireType={activeType}
              onComplete={handleComplete}
              onBack={() => setView("list")}
            />
          )}
          {view === "result" && currentResult && (
            <ResultView
              result={currentResult}
              onBack={() => setView("list")}
              onRetake={handleRetake}
            />
          )}
          {view === "history" && (
            <HistoryView onBack={() => setView("list")} />
          )}
        </div>
      </main>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.4s ease-out; }
      `}</style>
    </div>
  );
};

const DeepDiveGuarded = () => (
  <PremiumPageGuard feature="Deep Dive Clinical Assessments" requiredTier="diamond">
    <DeepDive />
  </PremiumPageGuard>
);

export default DeepDiveGuarded;
